import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, LayoutGroup } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router";

import { PortfolioSidebar } from "@/components/PortfolioSidebar/PortfolioSidebar";
import { ProjectSidebar } from "@/components/ProjectSidebar/ProjectSidebar";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { LOCAL_STORAGE_KEYS } from "@/helpers/storage";
import { useBool } from "@/hooks/useBool";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { getLocalStorageValue, updateLocalStorage, useUpdateLocalStorage } from "@/hooks/useLocalStorage";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";

import { MobileMenuBar } from "./MobileMenuBar";

type MenuType = "portfolio" | "project";

const SIDEBAR_STALE_MS = 30 * 60 * 1000;

export function NavigationSidebar(): React.ReactNode {
  // Mobile only
  const [openedMobileMenu, setOpenedMobileMenu] = useState<MenuType | null>(null);
  // Desktop only — single collapsed state shared across portfolio and project sidebars
  const [isSidebarCollapsed, sidebarCollapsedHandlers] = useBool(() =>
    Date.now() - getLocalStorageValue(LOCAL_STORAGE_KEYS.MENU_LAST_VISITED, 0) < SIDEBAR_STALE_MS
      ? getLocalStorageValue(LOCAL_STORAGE_KEYS.MENU, false)
      : false,
  );
  useUpdateLocalStorage(LOCAL_STORAGE_KEYS.MENU, isSidebarCollapsed);

  // Stamp the current visit so quick refreshes restore state
  useEffect(() => {
    updateLocalStorage(LOCAL_STORAGE_KEYS.MENU_LAST_VISITED, Date.now());
  }, []);

  const params = useParams();
  const isInProject = !!params["slug"];
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const selfAccountQueries = useSelfAccountQueries();
  const { data: linkedProjects } = useQuery(selfAccountQueries.listLinkedProjects());

  // Mobile only: close the sidebar when navigating to another page
  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setOpenedMobileMenu(null);
  }, [location.pathname]);

  // Mobile only
  const onToggleOpenedMenu = (type: MenuType, state: boolean) => {
    if (isDesktop) return;

    setOpenedMobileMenu(state ? type : null);
  };

  // If the user has no linked projects other than resident ones, we can hide the portfolio sidebar and directly show the project sidebar
  const canViewPortfolio = useMemo(() => hasAdminProject(linkedProjects), [linkedProjects]);

  return (
    <nav className="flex md:grid md:h-full md:auto-cols-min md:grid-flow-col">
      {!isDesktop && (
        <MobileMenuBar
          onToggleMenu={() => onToggleOpenedMenu(isInProject ? "project" : "portfolio", true)}
          isInProject={isInProject}
        />
      )}
      <AnimatePresence mode="wait">
        <LayoutGroup>
          {canViewPortfolio && (
            <PortfolioSidebar
              isOpened={isDesktop || (!isInProject && openedMobileMenu === "portfolio")}
              onToggleOpen={(state) => onToggleOpenedMenu("portfolio", state)}
              isCollapsed={isDesktop && (isInProject || isSidebarCollapsed)}
              onToggleCollapse={sidebarCollapsedHandlers.set}
              isFaded={isInProject}
            />
          )}
          {isInProject && (
            <ProjectSidebar
              isOpened={isDesktop || (isInProject && openedMobileMenu === "project")}
              onToggleOpen={(state) => onToggleOpenedMenu("project", state)}
              isCollapsed={isDesktop && isSidebarCollapsed}
              onToggleCollapse={sidebarCollapsedHandlers.set}
            />
          )}
        </LayoutGroup>
      </AnimatePresence>
    </nav>
  );
}
